import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Lightbulb } from 'lucide-react';

interface TodayInsightCardProps {
  title: string;
  body: string;
  levelTags: string[];
}

export const TodayInsightCard: React.FC<TodayInsightCardProps> = ({
  title,
  body,
  levelTags
}) => {
  const getLevelColor = (level: string) => {
    const lowerLevel = level.toLowerCase();
    if (lowerLevel.includes('physical')) return 'bg-accent/10 text-accent border-accent/20';
    if (lowerLevel.includes('mental')) return 'bg-primary/10 text-primary border-primary/20';
    if (lowerLevel.includes('emotional')) return 'bg-secondary/10 text-secondary border-secondary/20';
    if (lowerLevel.includes('spiritual')) return 'bg-muted/20 text-muted-foreground border-muted/30';
    if (lowerLevel.includes('relational')) return 'bg-accent/15 text-accent border-accent/25';
    return 'bg-muted/15 text-muted-foreground border-muted/25';
  };

  return (
    <Card className="relative border-0 bg-gradient-to-br from-card/80 to-card/40 backdrop-blur-sm shadow-lg overflow-hidden">
      {/* Decorative accent */}
      <div className="absolute top-0 left-0 w-1 h-full bg-gradient-to-b from-primary via-accent to-secondary" />
      <div className="absolute top-0 right-0 w-24 h-24 bg-gradient-radial from-primary/5 to-transparent rounded-full -translate-y-1/2 translate-x-1/2" />
      
      <CardContent className="p-6 sm:p-8 pl-8">
        <div className="space-y-5">
          {/* Header */}
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
              <Lightbulb className="w-5 h-5 text-primary" />
            </div>
            <div>
              <span className="text-xs font-light text-muted-foreground tracking-widest uppercase">Today's Insight</span>
            </div>
          </div>
          
          {/* Content */}
          <p className="text-base sm:text-lg text-foreground font-light leading-relaxed">
            {body}
          </p>
          
          {/* Tags */}
          {levelTags.length > 0 && (
            <div className="flex flex-wrap gap-2 pt-2">
              {levelTags.map((tag, idx) => (
                <Badge 
                  key={idx} 
                  variant="outline" 
                  className={`${getLevelColor(tag)} font-light text-xs tracking-wide`}
                >
                  {tag}
                </Badge>
              ))}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};
